import { useCallback, useEffect, useState } from "react";
import { getStorageDownloadURL } from "../Firebase/firebase";
import { resolveAttestationVideoUrl } from "./videoSource";

const READABLE_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

export const useAttestationDetails = (firebase, uid) => {
  const [kycData, setKycData] = useState(null);
  const [existingVideoUrl, setExistingVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!uid || !firebase) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    firebase.db
      .collection("users")
      .doc(uid)
      .collection("KYC")
      .doc("KYC")
      .get({ source: "server" })
      .then(async (doc) => {
        if (!active) return;
        const data = doc.data() ?? null;
        setKycData(data);
        if (!data?.attestationVideo) return;
        try {
          const url = await resolveAttestationVideoUrl(data.attestationVideo, getStorageDownloadURL);
          if (active && url) setExistingVideoUrl(url);
        } catch {
          if (active) setExistingVideoUrl(null);
        }
      })
      .catch(() => {
        if (active) setError("We couldn't load your verification details. Please retry before continuing.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [firebase, uid, retryCount]);

  const retry = useCallback(() => setRetryCount((count) => count + 1), []);

  return {
    loading,
    error,
    retry,
    existingVideoUrl,
    isAttestationExcluded: kycData?.attestationVideoExcluded === true,
    submittedFrontIdPath: getSubmittedFrontIdPath(kycData?.idImages || []),
    script: {
      fullName: getFullName(kycData?.name),
      birthDate: formatReadableDate(kycData?.dateOfBirth),
      today: READABLE_DATE_FORMATTER.format(new Date()),
    },
  };
};

export const useIdPreview = (submittedFrontIdPath) => {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const open = useCallback(async () => {
    setIsOpen(true);
    setError("");

    if (!submittedFrontIdPath) {
      setLoading(false);
      setUrl(null);
      setError("We couldn't find the submitted front side of the ID for this application.");
      return;
    }
    if (url) return;

    setLoading(true);
    try {
      setUrl(await getStorageDownloadURL(submittedFrontIdPath));
    } catch {
      setUrl(null);
      setError("We couldn't load the submitted ID preview right now. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [submittedFrontIdPath, url]);

  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, url, loading, error, open, close };
};

const formatReadableDate = (value) => {
  if (!value) return "";

  try {
    const date = typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? "" : READABLE_DATE_FORMATTER.format(date);
  } catch {
    return "";
  }
};

const getFullName = (name = {}) =>
  [name?.firstName, name?.middleName, name?.lastName, name?.suffix]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join(" ");

const getSubmittedFrontIdPath = (idImages = []) =>
  idImages.find((item) => item?.image === "idPhotoFront" || item?.image === "secondary1")?.url || null;
